import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { TSearchParams } from "@sembark-travel/ui-list"
import { queryToSearch } from "@sembark-travel/ui-router"
import type { TAuthLogsRequest } from "./store"

export const AUTH_LOG_EVENTS: Array<[string, string]> = [
	["login", "Logged in"],
	["login_failed", "Login failed"],
	["login_locked_out", "Locked out"],
	["logout", "Logged out"],
	["sessions_revoked", "Other sessions ended"],
	["signup", "Signed up"],
	["invite_accepted", "Accepted invite"],
	["email_verified", "Verified email"],
	["email_changed", "Changed email"],
	["password_changed", "Changed password"],
	["password_reset_requested", "Requested password reset"],
	["password_reset", "Reset password"],
	["password_login_disabled", "Disabled password login"],
	["password_login_enabled", "Enabled password login"],
	["mfa_challenge", "Asked for 2FA code"],
	["mfa_failed", "2FA failed"],
	["mfa_enabled", "Enabled 2FA"],
	["mfa_disabled", "Disabled 2FA"],
	["mfa_policy_changed", "Changed 2FA policy"],
	["passkey_added", "Added passkey"],
	["passkey_removed", "Removed passkey"],
	["user_disabled", "Account disabled"],
	["user_enabled", "Account enabled"],
]

const EVENT_LABELS = Object.fromEntries(AUTH_LOG_EVENTS)

export function authLogEventLabel(event: string): string {
	return EVENT_LABELS[event] || event.replace(/_/g, " ")
}

export function authLogFailureLabel(reason?: string | null): string | null {
	return reason ? reason.replace(/_/g, " ") : null
}

export type TAuthLogFilters = TSearchParams & {
	event?: string
	country?: string
	outside_india?: boolean
	created_after?: Date
	created_before?: Date
	tenants?: Array<{ id: number; name: string }>
}

export type TAuthLogFiltersInQuery = TSearchParams & {
	event?: string
	country?: string
	oi?: 1
	ca?: string
	cb?: string
	tenants?: Array<string>
}

export function authLogFiltersToQuery(
	filters: TAuthLogFilters
): TAuthLogFiltersInQuery {
	const { q, event, country, outside_india, created_after, created_before } =
		filters
	const query: TAuthLogFiltersInQuery = {}
	if (q) query.q = q
	if (event) query.event = event
	if (country) query.country = country
	if (outside_india) query.oi = 1
	if (created_after) query.ca = dateToQuery(created_after)
	if (created_before) query.cb = dateToQuery(created_before)
	if (filters.tenants?.length) {
		query.tenants = filters.tenants.map((t) => `${t.id}__${t.name}`)
	}
	return query
}

export function authLogQueryToFilters(
	query: TAuthLogFiltersInQuery
): TAuthLogFilters {
	const { q, event, country, oi, ca, cb } = query
	const filters: TAuthLogFilters = {}
	if (q) filters.q = q
	if (event) filters.event = event
	if (country) filters.country = country
	if (oi) filters.outside_india = true
	if (ca) filters.created_after = parseDateFromQuery(ca)
	if (cb) filters.created_before = parseDateFromQuery(cb)
	if (query.tenants?.length) {
		filters.tenants = query.tenants.map((t) => {
			const [id, ...name] = t.split("__")
			return { id: Number(id), name: name.join("__") }
		})
	}
	return filters
}

/**
 * Search string for the all-tenants auth logs page with these filters,
 * e.g. to drill down from a report.
 */
export function authLogsSearchFor(filters: TAuthLogFilters): string {
	return queryToSearch(authLogFiltersToQuery(filters))
}

export function authLogFiltersToRequest(
	filters: TAuthLogFilters
): TAuthLogsRequest {
	const { q, cursor, event, country, outside_india } = filters
	return {
		cursor,
		q: q || undefined,
		events: event ? [event] : undefined,
		country: country ? country.trim().toUpperCase() : null,
		// MaxMind sets the country; rows with no country are left out
		exclude_country: outside_india ? "IN" : null,
		created_after: filters.created_after
			? dateToUTCString(startOf(filters.created_after, "day"))
			: null,
		created_before: filters.created_before
			? dateToUTCString(endOf(filters.created_before, "day"))
			: null,
		tenants: filters.tenants?.length
			? filters.tenants.map((t) => t.id)
			: undefined,
	}
}
